import { createPortal } from 'react-dom';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { UILoader } from '@futrli/ui-components';

import { NumberExplainedMessagesIds } from './number-explained-messages';
import { NumberExplainedResponse } from './types';
import { NumberExplainedContent } from './number-explained-content';
import { NumberExplainedByMessageId } from './number-explained-for-message-id';
import styles from './number-explained.scss';

export interface NumberExplainedPopoverProps {
  id: string;
  open: boolean;
  loading: boolean;
  anchorEl: HTMLDivElement | null;
  data: (NumberExplainedResponse & { actual?: number; predicted?: number; accountName?: string }) | null;
  error?: Error | null;
}

export const NumberExplainedPopover: FC<NumberExplainedPopoverProps> = ({
  id,
  anchorEl,
  open,
  loading,
  error,
  data
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);

  const updatePosition = useCallback(() => {
    if (anchorEl && popoverRef.current) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popperHeight = popoverRef.current.offsetHeight;

      const offsetY = 8; // space above anchor
      const offsetX = 75; // shift left from anchor

      const top = window.scrollY + anchorRect.top - popperHeight - offsetY;
      const left = window.scrollX + anchorRect.left - offsetX;

      setPosition({ top, left });
      setReady(true);
    }
  }, [anchorEl]);

  useEffect(() => {
    if (open) {
      setReady(false);
      requestAnimationFrame(() => updatePosition());
    }
  }, [open, anchorEl, updatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handle = () => updatePosition();
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [open, anchorEl, updatePosition]);

  const renderError = () => {
    return <NumberExplainedByMessageId messageId={NumberExplainedMessagesIds.ErrorMessage} />;
  };

  if (!open || !anchorEl) {
    return null;
  }

  return createPortal(
    <div
      id={id}
      ref={popoverRef}
      className={styles.numberExplainedPopper}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        zIndex: 13,
        visibility: ready ? 'visible' : 'hidden',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)'
      }}
    >
      <div className={styles.numberExplainedPaper}>
        {loading && <UILoader />}
        {!loading && error && renderError()}
        {!loading && !error && <NumberExplainedContent data={data} />}
      </div>
    </div>,
    document.body
  );
};
